import type { UiState } from 'instantsearch.js';
import type { IndexUiState } from 'instantsearch.js/es/types/ui-state';

export type ALGOLIA_INDEX_DATA_TYPE = 'PRODUCT' | 'STORE' | 'SERVICE_CENTER';

export type STORE_TYPE =
  | 'BOUTIQUE'
  | 'FLAG_SHIP_BOUTIQUE'
  | 'OFFICIAL_RETAILER'
  | 'OUTLETS'
  | 'SERVICE_CENTER';

export const searchableAttributes = [
  'collection',
  'type',
  'size',
  'case',
  'dial_color',
  'sku',
  'distance_between_horns_en',
  'color',
  'material',
  'strap_length',
  'buckle_type',
] as const;

export enum MarketingStatus {
  LIMITED = 'Limited',
  COUNTRY_EXCLUSIVE = 'Country Exclusive',
  ONLINE_EXCLUSIVE = 'Online Exclusive',
  BOUTIQUE_AND_ONLINE_EXCLUSIVE = 'Boutique & Online Exclusive',
  BOUTIQUE_EXCLUSIVE = 'Boutique Exclusive',
  CAPSULE = 'Capsule',
}

export const enum AlgoliaEventsNames {
  clickedObjectIDsAfterSearch = 'clickedObjectIDsAfterSearch',
  convertedObjectIDsAfterSearch = 'convertedObjectIDsAfterSearch',
  addedToCartObjectIDsAfterSearch = 'addedToCartObjectIDsAfterSearch',
  purchasedObjectIDsAfterSearch = 'purchasedObjectIDsAfterSearch',
}

export type SearchableAttribute = (typeof searchableAttributes)[number];

export type HighlightResult = {
  matchLevel: 'full' | 'none';
  matchedWords: string[];
  value: string;
};

export type WatchHit = {
  _highlightResult: Record<string, HighlightResult>;
  call_to_action: string;
  case: Record<string, string>;
  collection: string;
  collection_id: string;
  collections: {
    name: string;
    slug: string;
  }[];
  components_expire_ts: number | null;
  components_preview: boolean;
  components_release_ts: number | null;
  country_code?: string;
  currency: string;
  designer_choice: boolean;
  dial_color: Record<string, string>;
  ecomm_available: boolean;
  has_launch_date: boolean;
  image_url: string | null;
  launch_date: number;
  limited_edition: boolean;
  marketing_status: string[] | null;
  model: {
    name: {
      [key: string]: string;
    };
    slug: string;
  };
  name: Record<string, string>;
  objectID: string; // SKU
  pdp_expire_ts: number | null;
  pdp_preview: boolean;
  pdp_release_ts: number | null;
  phone_sale_available: boolean;
  plp_expire_ts: number | null;
  plp_preview: boolean;
  plp_release_ts: number | null;
  price: number;
  price_range: string;
  product_type: string;
  publication_date: number;
  search_expire_ts: number | null;
  search_preview: boolean;
  search_release_ts: number | null;
  size: number;
  size_price: string;
  size_range: string;
  thumbnail_url: string | null;
  type: Record<string, string[]>;
  variant_id: string;
  widget_expire_ts: number | null;
  widget_preview: boolean;
  widget_release_ts: number | null;
};

export type TemporaryHours = {
  holiday_hours: Array<{
    date: string;
    open_intervals: Array<{ end: string; start: string }>;
  }>;
  reopen_date: string | null;
};

export type StrapHit = {
  _highlightResult: Record<string, HighlightResult>;
  buckle_type: {
    [key: string]: string;
  };
  call_to_action: string;
  color: {
    [key: string]: string;
  };
  country_code?: string;
  currency: string;
  distance_between_horns: string;
  ecomm_available: boolean;
  integrated_buckle: boolean;
  lug_size: string;
  material: {
    [key: string]: string;
  };
  material_en: string;
  name: {
    [key: string]: string;
  };
  objectID: string;
  other_sizes: string[];
  pdp_expire_ts: number | null;
  pdp_preview: boolean;
  pdp_release_ts: number | null;
  // SKU
  phone_sale_available: boolean;
  plp_expire_ts: number | null;
  plp_preview: boolean;
  plp_release_ts: number | null;
  price: number;
  price_range: string;
  product_type: string;
  publication_date: number;
  search_expire_ts: number | null;
  search_preview: boolean;
  search_release_ts: number | null;
  strap_length: {
    [key: string]: string;
  };
  thumbnail_url: string | null;
  variant_group: string[];
  variant_id: string;
};

/**
 * Eyewear product hit from Algolia
 */
export type EyewearHit = {
  _highlightResult?: Record<string, HighlightResult>;
  bridge_width: string;
  call_to_action: string;
  call_to_action_for_phone: string;
  call_to_action_weight: number;
  commercial_status: string;
  components_expire_ts: number | null;
  components_preview: boolean;
  components_release_ts: number | null;
  country_code?: string;
  currency: string;
  deduplication_key: string;
  description: Record<string, string>;
  designer_choice: boolean;
  ecomm_available: boolean;
  frame_color: Record<string, string>;
  frame_color_en: string;
  frame_material: Record<string, string>;
  frame_material_en: string;
  frame_shape: Record<string, string>;
  frame_shape_en: string;
  frontal_pins: Record<string, string>;
  frontal_width: number;
  head_reference: string;
  image_rolled_up_url: string;
  image_url: string | null;
  launch_date: number;
  lens_color: Record<string, string>;
  lens_color_en: string;
  lens_width: string;
  marketing_status: string[] | null;
  model: {
    name: {
      [key: string]: string;
    };
    slug: string;
  };
  model_en: string;
  name: Record<string, string>;
  name_en: string;
  nose_pads: Record<string, string>;
  objectID: string;
  pdp_expire_ts: number | null;
  pdp_preview: boolean;
  pdp_release_ts: number | null;
  phone_sale_available: boolean;
  plp_expire_ts: number | null;
  plp_preview: boolean;
  plp_release_ts: number | null;
  price: number;
  price_range: string;
  product_id: string;
  product_type: string;
  publication_date: number;
  reference: string;
  search_expire_ts: number | null;
  search_preview: boolean;
  search_release_ts: number | null;
  short_description: Record<string, string>;
  temple_length: string;
  temple_tip_detail: Record<string, string>;
  thumbnail_url: string | null;
  type: Record<string, string[]>;
  type_en?: string[];
  uv_protection: Record<string, string>;
  variant_id: string;
  weight: number;
  weight_unit: string;
  widget_expire_ts: number | null;
  widget_preview: boolean;
  widget_release_ts: number | null;
  widget_stock_in_wholesaler_ids: string[];
};

export type OpenInterval = {
  end: string;
  start: string;
};

export type DaySchedule = {
  isClosed?: boolean;
  openIntervals: OpenInterval[];
};

export type WeekSchedule = {
  friday: DaySchedule;
  monday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  thursday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
};

export type StoreHit = {
  _geoloc?: { lat: number; lng: number };
  _highlightResult: Record<string, HighlightResult>;
  appointments: object;
  city: string;
  city_de: string;
  city_en: string;
  city_es: string;
  city_fr: string;
  city_it: string;
  city_ja: string;
  city_ko: string;
  city_pt: string;
  city_ru: string;
  city_zh_Hans: string;
  city_zh_Hant: string;
  country_code: string;
  description: string;
  description_de: string;
  description_en: string;
  description_es: string;
  description_fr: string;
  description_it: string;
  description_ja: string;
  description_zh_Hans: string;
  description_zh_Hant: string;
  emails: string[];
  fax: string;
  focal_point: { x: number; y: number };
  hours: WeekSchedule;
  image_dimensions: { x: number; y: number };
  is_highlighted: boolean;
  lat: number;
  long: number;
  name: string;
  name_de: string;
  name_en: string;
  name_es: string;
  name_fr: string;
  name_it: string;
  name_ja: string;
  name_ko: string;
  name_pt: string;
  name_ru: string;
  name_zh_Hans: string;
  name_zh_Hant: string;
  objectID: string;
  offers_appointment: boolean;
  offers_boutique_pickup: boolean;
  operating_type?: string;
  phone: string | null;
  photo_gallery: ImageWithTypeScript[];
  postal_code: string;
  region: string;
  region_de?: string;
  region_en?: string;
  region_fr?: string;
  region_it?: string;
  region_ja?: string;
  region_ko?: string;
  region_pl?: string;
  region_pt?: string;
  region_ru?: string;
  region_zh_Hans?: string;
  region_zh_Hant?: string;
  service_your_watch: boolean;
  slug: string;
  store_id: string;
  street: string;
  street2: string;
  street_de: string;
  street_en: string;
  street_fr: string;
  street_it: string;
  street_ja: string;
  street_ko: string;
  street_pl: string;
  street_pt: string;
  street_ru: string;
  street_zh_Hans: string;
  street_zh_Hant: string;
  temporary_hours?: TemporaryHours;
  thumbnail_url: string | null;
  type: string;
  utc_offset: string;
};

// Removed duplicate EyewearHit definition. See the consolidated version above.

export type AlgoliaHit =
  | WatchHit
  | StrapHit
  | StoreHit
  | ContactHit
  | EyewearHit;

export type SearchHit = WatchHit | StrapHit | StoreHit | EyewearHit;

export type ProductHit = WatchHit | StrapHit | EyewearHit;

export type SearchUiState = UiState & {
  [indexId: string]: IndexUiState & {
    range?: {
      price?: string;
    };
    refinementList?: Partial<Record<SearchableAttribute, string[]>>;
  };
};

type ImageInfo = {
  url: string;
};

type ImageWithTypeScript = {
  image: ImageInfo;
};
export interface BoutiqueSearchResult {
  _distinctSeqID?: number;
  _geoloc?: { lat: number; lng: number };
  appointments?: string[];
  city?: string;
  country_code?: string;
  description?: string;
  emails?: string[] | undefined;
  fax?: string;
  hours?: any;
  name?: string;
  name_de?: string;
  name_en?: string;
  name_es?: string;
  name_fr?: string;
  name_it?: string;
  name_ja?: string;
  name_ko?: string;
  name_pt?: string;
  name_zh_Hans?: string;
  name_zh_Hant?: string;
  objectID: string;
  offers_appointment?: boolean;
  offers_boutique_pickup?: boolean;
  phone?: string;
  photo_gallery?: any;
  postal_code?: string;
  region?: string;
  service_your_watch?: boolean;
  slug?: string;
  store_id?: string;
  street?: string;
  thumbnail_url?: string;
  type?: string;
  utc_offset?: string;
}

export type WeekendInterval = {
  isClosed?: boolean;
  openIntervals?: OpenInterval[];
};

export type WorkingHours = {
  friday?: DaySchedule;
  holidayHours?: WorkingHoursHoliday[];
  monday?: DaySchedule;
  saturday?: WeekendInterval;
  sunday?: WeekendInterval;
  thursday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
};
export type WorkingHoursHoliday = {
  date: string;
  isClosed?: boolean;
  openIntervals: OpenInterval[];
};
export type ContactHit = {
  _highlightResult: Record<string, HighlightResult>;
  email: string;
  hours: WorkingHours;
  objectID: string;
  phone_number: string;
};
export type AlgoliaEvent = {
  currency?: string;
  event: AlgoliaEventsNames;
  indexName?: string;
  objectIDs: string[];
  positions?: number[];
  queryID?: string;
  value?: number;
};

export enum PAGE_CONTEXT {
  PLP_EYEWEAR,
  PLP_WATCHES,
}
